import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../common/utils/routes';
import api from '../../apiServices';
import { FormulaActivityAttributes, FormulaAttributes } from '../../types/apiType';
import { showNotification } from '../common/CommonSlice';
import { PaginatedConnection, PaginatedQueryParams } from '../../types/common';

export const useAddFormulaMutation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const addFormula = async (value: FormulaAttributes) => {
    const res: string = await api.formula.addFormula(value);
    return res;
  };
  return useMutation({
    mutationFn: addFormula,
    onSuccess: async () => {
      navigate(routes.DEFAULT);
      dispatch(showNotification({ message: 'Lưu công thức thàng công', type: 'success' }));
      await queryClient.invalidateQueries({
        queryKey: ['formula/getListFormula'],
      });
    },
    onError: (error) => {
      const message = get(error, 'response.data.message');
      if (message) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    },
  });
};

export const useUpdateFormulaMutation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const updateFormula = async ({ id, value }: { id: string; value: FormulaAttributes }) => {
    const res: string = await api.formula.updateFormula(id, value);
    return res;
  };
  return useMutation({
    mutationFn: updateFormula,
    onSuccess: async () => {
      navigate(routes.DEFAULT);
      dispatch(showNotification({ message: 'Lưu công thức thàng công', type: 'success' }));
      await queryClient.invalidateQueries({
        queryKey: ['formula/getListFormula'],
      });
    },
    onError: (error) => {
      const message = get(error, 'response.data.message');
      if (message) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    },
  });
};

export const useDeleteFormulaMutation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const deleteFormula = async (id: string) => {
    const res: string = await api.formula.deleteFormula(id);
    return res;
  };
  return useMutation({
    mutationFn: deleteFormula,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['formula/getListFormula'],
      });
      dispatch(showNotification({ message: 'Xoá công thức thàng công', type: 'success' }));
      navigate(routes.DEFAULT);
    },
    onError: (error) => {
      const message = get(error, 'response.data.message');
      if (message) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    },
  });
};

export const useFormulaPaginatedQuery = (pageParam: PaginatedQueryParams) => {
  const dispatch = useDispatch();

  const getUserList = async ({ pageParam }: { pageParam: PaginatedQueryParams }) => {
    const user: PaginatedConnection<FormulaAttributes> = await api.formula.getListFormula({ ...pageParam });
    return user;
  };

  return useQuery({
    queryKey: ['formula/getListFormula', pageParam],
    queryFn: () => getUserList({ pageParam }),
    keepPreviousData: true,
    onError: (error) => {
      const message = get(error, 'response.data.message');
      if (message) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    },
  });
};

export const usePrintFormulaMutation = () => {
  const dispatch = useDispatch();

  const mutationFn = async (params: { id: string; name: string; quantity: number }) => {
    const res: string = await api.formula.printFormulaSuccess(params);
    return res;
  };

  return useMutation({
    mutationFn,
    onError: (error) => {
      const message = get(error, 'response.data.message');
      if (message) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    },
  });
};

export const useFormulaActivityPaginatedQuery = (pageParam: PaginatedQueryParams & { date: string }) => {
  const dispatch = useDispatch();

  const queryFn = async () => {
    const data: PaginatedConnection<FormulaActivityAttributes> = await api.formula.getListFormulaActivity({ ...pageParam });
    return data;
  };

  return useQuery({
    queryKey: ['formula/getListFormulaActivity', pageParam],
    queryFn,
    keepPreviousData: true,
    onError: (error) => {
      const message = get(error, 'response.data.message');
      if (message) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    },
    staleTime: 30000,
  });
};

export const useUpdateFormulaActivityMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const mutationFn = async (params: { id: string; value: Pick<FormulaActivityAttributes, 'colors' | 'chemicals'> }) => {
    const res: string = await api.formula.updateFormulaActivity(params.id, params.value);
    return res;
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      dispatch(showNotification({ message: 'Lưu thàng công', type: 'success' }));
      queryClient.invalidateQueries({
        queryKey: ['formula/getListFormulaActivity'],
      });
    },
    onError: (error) => {
      const message = get(error, 'response.data.message');
      if (message) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    },
  });
};

export const useDeleteFormulaActivityMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutationFn = async (id: string) => {
    const res: string = await api.formula.deleteFormulaActivity(id);
    return res;
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      dispatch(showNotification({ message: 'Xoá thàng công', type: 'success' }));
      queryClient.invalidateQueries({
        queryKey: ['formula/getListFormulaActivity'],
      });
    },
  });
};
